import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  ConfirmButton,
  Pagination,
  QueryBoundary,
  Table,
  Td,
  Th,
  toneForSeverity,
} from '../components/ui';
import { atLeast } from '../lib/roles';
import { dateTime, titleCase } from '../lib/format';

const LIMIT = 100;

export default function Events() {
  const { user } = useAuth();
  const canAck = atLeast(user?.role, 'operator');
  const qc = useQueryClient();

  const [unackOnly, setUnackOnly] = useState(true);
  const [operatorOnly, setOperatorOnly] = useState(false);
  const [offset, setOffset] = useState(0);

  const q = useQuery({
    queryKey: ['events', unackOnly, operatorOnly, offset],
    queryFn: () =>
      api.events({
        acknowledged: unackOnly ? false : undefined,
        requires_operator: operatorOnly ? true : undefined,
        limit: LIMIT,
        offset,
      }),
    refetchInterval: 30_000,
  });

  const ack = useMutation({
    mutationFn: (id: number) => api.ackEvent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });

  return (
    <>
      <PageHeader
        title="Events"
        description="The incident stream (ARCHITECTURE §17). Halts, orphans, reconciliation breaks, gateway outages."
      />

      <div className="mb-4 flex flex-wrap gap-4 text-sm text-slate-400">
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={unackOnly}
            onChange={(e) => {
              setUnackOnly(e.target.checked);
              setOffset(0);
            }}
          />
          Unacknowledged only
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={operatorOnly}
            onChange={(e) => {
              setOperatorOnly(e.target.checked);
              setOffset(0);
            }}
          />
          Needs operator
        </label>
      </div>

      <QueryBoundary q={q} isEmpty={q.data?.items.length === 0} empty={<p className="text-sm text-emerald-400">Nothing outstanding.</p>}>
        {q.data && (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Time</Th>
                  <Th>Severity</Th>
                  <Th>Type</Th>
                  <Th>Scope</Th>
                  <Th>Message</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-800/30">
                    <Td className="whitespace-nowrap text-xs text-slate-400">{dateTime(e.created_at)}</Td>
                    <Td>
                      <Badge tone={toneForSeverity(e.severity)}>{e.severity}</Badge>
                    </Td>
                    <Td>
                      {titleCase(e.event_type)}
                      {e.requires_operator && <span className="ml-1 text-xs text-amber-400">!</span>}
                    </Td>
                    <Td className="text-xs text-slate-400">
                      {e.service ?? '—'}
                      {e.account_id != null && ` · acct ${e.account_id}`}
                      {e.symbol && ` · ${e.symbol}`}
                    </Td>
                    <Td className="text-slate-300">{e.message}</Td>
                    <Td>
                      {e.acknowledged ? (
                        <Badge tone="good">ack</Badge>
                      ) : canAck ? (
                        <ConfirmButton
                          title={`Acknowledge event ${e.id}?`}
                          body="Marks it handled. It stays in the log."
                          confirmLabel="Acknowledge"
                          onConfirm={() => ack.mutateAsync(e.id)}
                        >
                          Ack
                        </ConfirmButton>
                      ) : (
                        <Badge tone="warn">open</Badge>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination total={q.data.total} limit={LIMIT} offset={offset} onChange={setOffset} />
          </>
        )}
      </QueryBoundary>
    </>
  );
}
